import { Dictionary } from './Utils';
import { DataGrid } from './DataGrid';
import { GridSelection } from './GridSelection';

export enum SelectionBehavior {
  SelectCell,
  SelectColumn,
  SelectRow,
  None
}

export interface NumRange {
  begin: number;
  /** `Infinity` means "all" */
  end: number;
}

export interface CellRange {
  begin: DataGrid.Cell;
  end: DataGrid.Cell;
}

export interface GridDimensions {
  rows: number;
  cols: number;
}

export interface IndexBasedSelectionData {
  rowRanges: NumRange[];
  colRanges: NumRange[];
  cellRanges: CellRange[];
  gridDimensions: GridDimensions;
}

enum SelectionRangeType {
  Row,
  Col,
  Cell
}

/**
 * Tracks the state of selected cells/rows/cols. Provides methods to modify the selection and methods
 * to see if a cell/row/col is selected.
 */
export class IndexBasedGridSelection implements GridSelection {
  private selection: IndexBasedSelectionData;

  /**
   * The anchor point is used for ranged selection as the starting point. It is set from the row, col,
   * or cell selections. If it is a row or col selection, it is converted to a Cell by converting the row to
   * {rowIdx: row, cell: 0} and vice-versa.  It is initialized as {0, 0}
   */
  private anchorPoint: DataGrid.Cell = { rowIdx: 0, colIdx: 0 };

  /**
   * The last selection range type is tracked because for range selections the last selection is removed.
   */
  private lastSelectionRangeType: SelectionRangeType | null = null;

  public constructor(rows: number, cols: number) {
    this.selection = {
      rowRanges: [],
      colRanges: [],
      cellRanges: [],
      gridDimensions: { rows, cols }
    };
  }

  /** Clears selection, but does not reset the anchor point */
  public clearSelection(): void {
    this.selection.rowRanges = [];
    this.selection.colRanges = [];
    this.selection.cellRanges = [];
    this.lastSelectionRangeType = null;
  }

  public resetAnchorPoint(): void {
    this.anchorPoint = { rowIdx: 0, colIdx: 0 };
  }

  /**
   * This initializes the selection data, sets the last selection range type to null, and the anchor point
   * to {0, 0}.
   */
  public initializeSelection(selectionData: IndexBasedSelectionData): void {
    this.selection = selectionData;
    this.lastSelectionRangeType = null;
    this.resetAnchorPoint();
  }

  /**
   * This does not add a selection, but merely sets the anchor point for ranged selections.
   */
  public setAnchorPoint(anchorPoint: DataGrid.Cell): void {
    this.anchorPoint = anchorPoint;
  }

  public getSelection(): IndexBasedSelectionData {
    return this.selection;
  }

  public isSelected(cell: DataGrid.Cell): boolean {
    if (cell.colIdx === -1) {
      return IndexBasedGridSelection.isRowSelected(cell.rowIdx, this.selection);
    } else if (cell.rowIdx === -1) {
      return IndexBasedGridSelection.isColSelected(cell.colIdx, this.selection);
    } else {
      return IndexBasedGridSelection.isCellSelected(cell, this.selection);
    }
  }

  /*
   * Static Query Methods
   */

  public static isCellSelected(cell: DataGrid.Cell, selectedCells: IndexBasedSelectionData): boolean {
    return IndexBasedGridSelection.isRowSelected(cell.rowIdx, selectedCells) ||
      IndexBasedGridSelection.isColSelected(cell.colIdx, selectedCells) ||
      IndexBasedGridSelection.isWithinCellRanges(cell, selectedCells);
  }

  public static isRowSelected(row: number, selectedCells: IndexBasedSelectionData): boolean {
    return IndexBasedGridSelection.isInNumRanges(row, selectedCells.rowRanges) ||
      IndexBasedGridSelection.areAllCellsSelected(SelectionRangeType.Row, row, selectedCells, selectedCells.gridDimensions.cols);
  }

  public static isColSelected(col: number, selectedCells: IndexBasedSelectionData): boolean {
    return IndexBasedGridSelection.isInNumRanges(col, selectedCells.colRanges) ||
      IndexBasedGridSelection.areAllCellsSelected(SelectionRangeType.Col, col, selectedCells, selectedCells.gridDimensions.rows);
  }

  private static areAllCellsSelected(
    type: SelectionRangeType,
    index: number,
    selectionData: IndexBasedSelectionData,
    count: number
  ): boolean {
    if (type !== SelectionRangeType.Row && type !== SelectionRangeType.Col) { return false; }
    if (count <= 0) { return false; }

    const cellSelection: Dictionary<boolean> = {};

    const updateCellSelection = (begin: number, end: number): void => {
      end = Math.min(count, end);

      for (let idx = begin; idx <= end; idx++) {
        if (idx >= 0 && idx < count && !cellSelection[idx]) {
          cellSelection[idx] = true;
        }
      }
    }

    const numRanges = (type === SelectionRangeType.Row) ? selectionData.colRanges : selectionData.rowRanges;
    numRanges.forEach(numRange => {
      numRange = this.normalizeNumRange(numRange);
      updateCellSelection(numRange.begin, numRange.end);
    });

    let checkNumRange: NumRange;
    let updateNumRange: NumRange;

    selectionData.cellRanges.forEach(cellRange => {
      cellRange = this.normalizeCellRange(cellRange);

      if (type === SelectionRangeType.Row) {
        checkNumRange = { begin: cellRange.begin.rowIdx, end: cellRange.end.rowIdx };
        updateNumRange = { begin: cellRange.begin.colIdx, end: cellRange.end.colIdx };
      } else {
        checkNumRange = { begin: cellRange.begin.colIdx, end: cellRange.end.colIdx };
        updateNumRange = { begin: cellRange.begin.rowIdx, end: cellRange.end.rowIdx };
      }

      if (IndexBasedGridSelection.isBetweenNumRange(index, checkNumRange.begin, checkNumRange.end)) {
        updateCellSelection(updateNumRange.begin, updateNumRange.end);
      }
    });

    return Object.keys(cellSelection).length === count;
  }

  private static areAllCellsInGridSelected(selectionData: IndexBasedSelectionData) {
    for (let idx = 0; idx < selectionData.gridDimensions.rows; idx++) {
      if (!IndexBasedGridSelection.isRowSelected(idx, selectionData)) {
        return false;
      }
    }
    return true;
  }

  private static isInNumRanges(num: number, numRanges: NumRange[]): boolean {
    for (let i = 0; i < numRanges.length; i++) {
      if (IndexBasedGridSelection.isInNumRange(num, numRanges[i])) {
        return true;
      }
    }
    return false;
  }

  private static isInNumRange(num: number, numRange: NumRange): boolean {
    numRange = IndexBasedGridSelection.normalizeNumRange(numRange);
    return IndexBasedGridSelection.isBetweenNumRange(num, numRange.begin, numRange.end);
  }

  private static isWithinCellRanges(cell: DataGrid.Cell, selectedCells: IndexBasedSelectionData): boolean {
    for (let i = 0; i < selectedCells.cellRanges.length; i++) {
      if (IndexBasedGridSelection.isWithinCellRange(cell, selectedCells.cellRanges[i])) {
        return true;
      }
    }
    return false;
  }

  private static isWithinCellRange(cell: DataGrid.Cell, cellRange: CellRange): boolean {
    cellRange = IndexBasedGridSelection.normalizeCellRange(cellRange);
    const withinCol = IndexBasedGridSelection.isBetweenNumRange(cell.colIdx, cellRange.begin.colIdx, cellRange.end.colIdx);
    const withinRow = IndexBasedGridSelection.isBetweenNumRange(cell.rowIdx, cellRange.begin.rowIdx, cellRange.end.rowIdx);
    return withinCol && withinRow;
  }

  private static isBetweenNumRange(num: number, begin: number, end: number): boolean {
    return (num >= begin && num <= end) || (num >= end && num <= begin);
  }

  private static normalizeNumRange(numRange: NumRange): NumRange {
    return numRange.begin <= numRange.end ? numRange : { begin: numRange.end, end: numRange.begin };
  }

  private static normalizeCellRange(cellRange: CellRange): CellRange {
    const beginCell = cellRange.begin;
    const endCell = cellRange.end;

    if (beginCell.rowIdx <= endCell.rowIdx && beginCell.colIdx <= endCell.colIdx) {
      return cellRange;
    } else if (endCell.rowIdx <= beginCell.rowIdx && endCell.colIdx <= beginCell.colIdx) {
      return {
        begin: endCell,
        end: beginCell
      };
    } else if (endCell.rowIdx <= beginCell.rowIdx && beginCell.colIdx <= endCell.colIdx) {
      return {
        begin: {
          rowIdx: endCell.rowIdx,
          colIdx: beginCell.colIdx
        },
        end: {
          rowIdx: beginCell.rowIdx,
          colIdx: endCell.colIdx
        }
      };
    }

    return {
      begin: {
        rowIdx: beginCell.rowIdx,
        colIdx: endCell.colIdx
      },
      end: {
        rowIdx: endCell.rowIdx,
        colIdx: beginCell.colIdx
      }
    };
  }

  /*
   * Select actions
   */

  public deselectRow(row: number): void {
    this.removeRow(row);
    this.removeCellsFromRow(row);
    this.removeSelectedCols(row);
  }

  public toggleRowSelect(row: number): void {
    if (IndexBasedGridSelection.isRowSelected(row, this.selection)) {
      this.deselectRow(row);
    } else {
      this.inclusiveRowSelect(row);
    }
  }

  public deselectColumn(col: number): void {
    this.removeColumn(col);
    this.removeCellsFromColumn(col);
    this.removeSelectedRows(col);
  }

  public toggleColumnSelect(col: number): void {
    if (IndexBasedGridSelection.isColSelected(col, this.selection)) {
      this.deselectColumn(col);
    } else {
      this.inclusiveColSelect(col);
    }
  }

  public deselectCell(cell: DataGrid.Cell): void {
    if (IndexBasedGridSelection.isRowSelected(cell.rowIdx, this.selection)) {
      this.removeRow(cell.rowIdx);
      this.addCellRangesForRemovedRow(cell);
    }

    if (IndexBasedGridSelection.isColSelected(cell.colIdx, this.selection)) {
      this.removeColumn(cell.colIdx);
      this.addCellRangesForRemovedColumn(cell);
    }

    this.removeCell(cell);
  }

  public toggleCellSelect(cell: DataGrid.Cell): void {
    if (IndexBasedGridSelection.isCellSelected(cell, this.selection)) {
      this.deselectCell(cell);
    } else {
      this.inclusiveCellSelect(cell);
    }
  }

  /**
   * This happens typically when ctrl + pointerPressing.  It adds to the selected cells and the sets it as the
   * last selected cell.
   */
  public inclusiveCellSelect(cell: DataGrid.Cell): void {
    this.selection.cellRanges.push({ begin: cell, end: cell });
    this.lastSelectionRangeType = SelectionRangeType.Cell;
    this.setAnchorPointFromCell(cell);
  }

  /**
   * This happens typically when pointerPressing or navigating with arrow keys.  It clears the selected cells,
   * then adds the cell to selected cells, and then sets it as the last selected cell.
   */
  public exclusiveCellSelect(cell: DataGrid.Cell): void {
    this.clearSelection();
    this.inclusiveCellSelect(cell);
  }

  /**
   * This happens typically when ctrl + pointerPressing on the row header.  It adds to the selected rows and the sets it as the
   * last selected row.
   */
  public inclusiveRowSelect(row: number): void {
    this.selection.rowRanges.push({ begin: row, end: row });
    this.lastSelectionRangeType = SelectionRangeType.Row;
    this.setAnchorPointFromRow(row);
  }

  /**
   * Clears out any current selection and replace it with the new specified selection.
   */
  public exclusiveRowsSelect(rows: number[]): void {
    this.clearSelection();
    for (let row of rows) {
      this.inclusiveRowSelect(row);
    }
  }

  /**
   * This happens typically when pointerPressing on the row header.  It clears the selected cells, then adds the row to selected
   * rows, and then sets it as the last selected row.
   */
  public exclusiveRowSelect(row: number): void {
    this.clearSelection();
    this.inclusiveRowSelect(row);
  }

  /**
   * This happens typically when ctrl + pointerPressing on the col header.  It adds to the selected cols and the sets it as the
   * last selected col.
   */
  public inclusiveColSelect(col: number): void {
    this.selection.colRanges.push({ begin: col, end: col });
    this.lastSelectionRangeType = SelectionRangeType.Col;
    this.setAnchorPointFromCol(col);
  }

  /**
   * This happens typically when pointerPressing on the col header.  It clears the selected cells, then adds the col to selected
   * cols, and then sets it as the last selected col.
   */
  public exclusiveColSelect(col: number): void {
    this.clearSelection();
    this.inclusiveColSelect(col);
  }

  /**
   * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given row.
   * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
   */
  public rangeRowSelectFromAnchor(row: number): void {
    this.removeLastSelectedRangeIfNeeded();
    const rowRange = IndexBasedGridSelection.normalizeNumRange({ begin: this.anchorPoint.rowIdx, end: row });
    this.selection.rowRanges.push(rowRange);
    this.lastSelectionRangeType = SelectionRangeType.Row;
  }

  /**
   * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given coll.
   * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
   */
  public rangeColSelectFromAnchor(col: number): void {
    this.removeLastSelectedRangeIfNeeded();
    const colRange = IndexBasedGridSelection.normalizeNumRange({ begin: this.anchorPoint.colIdx, end: col });
    this.selection.colRanges.push(colRange);
    this.lastSelectionRangeType = SelectionRangeType.Col;
  }

  /**
   * Performs a range selection from the anchorPoint (typically last row/col/cell selection) to the given cell.
   * For a drag selection, do a row/col/cell selection on the starting point then do a range selection.
   */
  public rangeCellSelectFromAnchor(cell: DataGrid.Cell): void {
    this.removeLastSelectedRangeIfNeeded();
    const cellRange = IndexBasedGridSelection.normalizeCellRange({ begin: this.anchorPoint, end: cell });
    this.selection.cellRanges.push(cellRange);
    this.lastSelectionRangeType = SelectionRangeType.Cell;
  }

  /**
   * This method clears the selected cells, then adds the rowRange and colRange to the selected rows and cols.
   */
  public selectRowAndColRange(rowRange: NumRange, colRange: NumRange): void {
    this.clearSelection();
    this.selection.rowRanges.push(IndexBasedGridSelection.normalizeNumRange(rowRange));
    this.selection.colRanges.push(IndexBasedGridSelection.normalizeNumRange(colRange));
  }

  /**
   * This method selects the entire grid by selecting all rows.
   */
  public selectAll(): void {
    this.clearSelection();
    this.selection.rowRanges.push({ begin: 0, end: Infinity });
  }

  /**
   * This pops the last selection type from the selection.
   */
  private removeLastSelectedRangeIfNeeded(): void {
    switch (this.lastSelectionRangeType) {
      case SelectionRangeType.Cell:
        this.selection.cellRanges.pop();
        break;
      case SelectionRangeType.Col:
        this.selection.colRanges.pop();
        break;
      case SelectionRangeType.Row:
        this.selection.rowRanges.pop();
        break;
      default:
        return;
    }
  }

  private setAnchorPointFromCol(col: number): void {
    this.anchorPoint = { rowIdx: 0, colIdx: col };
  }

  private setAnchorPointFromRow(row: number): void {
    this.anchorPoint = { rowIdx: row, colIdx: 0 };
  }

  private setAnchorPointFromCell(cell: DataGrid.Cell): void {
    this.anchorPoint = cell;
  }

  private removeRow(row: number): void {
    let trimmedRowRanges: NumRange[] = [];

    this.selection.rowRanges.forEach(rowRange => {
      if (IndexBasedGridSelection.isInNumRange(row, rowRange)) {
        if (row - rowRange.begin > 0) {
          trimmedRowRanges.push({ begin: rowRange.begin, end: row - 1 });
        }
        if (rowRange.end - row > 0 || rowRange.end === Infinity) {
          trimmedRowRanges.push({ begin: row + 1, end: rowRange.end });
        }
      } else {
        trimmedRowRanges.push(rowRange);
      }
    });

    this.selection.rowRanges = trimmedRowRanges;
  }

  private removeColumn(column: number): void {
    let trimmedColRanges: NumRange[] = [];

    this.selection.colRanges.forEach(colRange => {
      if (IndexBasedGridSelection.isInNumRange(column, colRange)) {
        if (column - colRange.begin > 0) {
          trimmedColRanges.push({ begin: colRange.begin, end: column - 1 });
        }
        if (colRange.end - column > 0 || colRange.end === Infinity) {
          trimmedColRanges.push({ begin: column + 1, end: colRange.end });
        }
      } else {
        trimmedColRanges.push(colRange);
      }
    });

    this.selection.colRanges = trimmedColRanges;
  }

  private removeCell(cell: DataGrid.Cell): void {
    let trimmedCellRanges: CellRange[] = [];

    this.selection.cellRanges.forEach(cellRange => {
      if (IndexBasedGridSelection.isWithinCellRange(cell, cellRange)) {
        const beginCell = cellRange.begin;
        const endCell = cellRange.end;

        if (cell.rowIdx - beginCell.rowIdx > 0) {
          trimmedCellRanges.push({
            begin: beginCell,
            end: { rowIdx: cell.rowIdx - 1, colIdx: endCell.colIdx }
          });
        }

        if (endCell.rowIdx - cell.rowIdx > 0) {
          trimmedCellRanges.push({
            begin: { rowIdx: cell.rowIdx + 1, colIdx: beginCell.colIdx },
            end: endCell
          });
        }

        if (cell.colIdx - beginCell.colIdx > 0) {
          trimmedCellRanges.push({
            begin: { rowIdx: cell.rowIdx, colIdx: beginCell.colIdx },
            end: { rowIdx: cell.rowIdx, colIdx: cell.colIdx - 1 }
          });
        }

        if (endCell.colIdx - cell.colIdx > 0) {
          trimmedCellRanges.push({
            begin: { rowIdx: cell.rowIdx, colIdx: cell.colIdx + 1 },
            end: { rowIdx: cell.rowIdx, colIdx: endCell.colIdx }
          });
        }
      } else {
        trimmedCellRanges.push(cellRange);
      }
    });

    this.selection.cellRanges = trimmedCellRanges;
  }

  private removeCellsFromRow(row: number): void {
    let trimmedCellRanges: CellRange[] = [];

    this.selection.cellRanges.forEach(cellRange => {
      cellRange = IndexBasedGridSelection.normalizeCellRange(cellRange);
      if (IndexBasedGridSelection.isBetweenNumRange(row, cellRange.begin.rowIdx, cellRange.end.rowIdx)) {
        const beginCell = cellRange.begin;
        const endCell = cellRange.end;

        if (row - beginCell.rowIdx > 0) {
          trimmedCellRanges.push({
            begin: beginCell,
            end: { rowIdx: row - 1, colIdx: endCell.colIdx }
          });
        }

        if (endCell.rowIdx - row > 0) {
          trimmedCellRanges.push({
            begin: { rowIdx: row + 1, colIdx: beginCell.colIdx },
            end: endCell
          });
        }
      } else {
        trimmedCellRanges.push(cellRange);
      }
    });

    this.selection.cellRanges = trimmedCellRanges;
  }

  private removeSelectedCols(row: number): void {
    this.selection.colRanges.forEach(colRange => {
      if (row > 0) {
        this.selection.cellRanges.push({
          begin: { rowIdx: 0, colIdx: colRange.begin },
          end: { rowIdx: row - 1, colIdx: colRange.end }
        });
      }

      this.selection.cellRanges.push({
        begin: { rowIdx: row + 1, colIdx: colRange.begin },
        end: { rowIdx: Infinity, colIdx: colRange.end }
      });
    });

    this.selection.colRanges = [];
  }

  private removeCellsFromColumn(col: number): void {
    let trimmedCellRanges: CellRange[] = [];

    this.selection.cellRanges.forEach(cellRange => {
      cellRange = IndexBasedGridSelection.normalizeCellRange(cellRange);
      if (IndexBasedGridSelection.isBetweenNumRange(col, cellRange.begin.colIdx, cellRange.end.colIdx)) {
        const beginCell = cellRange.begin;
        const endCell = cellRange.end;

        if (col - beginCell.colIdx > 0) {
          trimmedCellRanges.push({
            begin: { rowIdx: beginCell.rowIdx, colIdx: beginCell.colIdx },
            end: { rowIdx: endCell.rowIdx, colIdx: col - 1 }
          });
        }

        if (endCell.colIdx - col > 0) {
          trimmedCellRanges.push({
            begin: { rowIdx: beginCell.rowIdx, colIdx: col + 1 },
            end: { rowIdx: endCell.rowIdx, colIdx: endCell.colIdx }
          });
        }
      } else {
        trimmedCellRanges.push(cellRange);
      }
    });

    this.selection.cellRanges = trimmedCellRanges;
  }

  private removeSelectedRows(col: number): void {
    this.selection.rowRanges.forEach(rowRange => {
      if (col > 0) {
        this.selection.cellRanges.push({
          begin: { rowIdx: rowRange.begin, colIdx: 0 },
          end: { rowIdx: rowRange.end, colIdx: col - 1 }
        });
      }

      this.selection.cellRanges.push({
        begin: { rowIdx: rowRange.begin, colIdx: col + 1 },
        end: { rowIdx: rowRange.end, colIdx: Infinity }
      });
    });

    this.selection.rowRanges = [];
  }

  private addCellRangesForRemovedRow(cell: DataGrid.Cell): void {
    if (cell.colIdx > 0) {
      this.selection.cellRanges.push({
        begin: { rowIdx: cell.rowIdx, colIdx: 0 },
        end: { rowIdx: cell.rowIdx, colIdx: cell.colIdx - 1 }
      });
    }

    this.selection.cellRanges.push({
      begin: { rowIdx: cell.rowIdx, colIdx: cell.colIdx + 1 },
      end: { rowIdx: cell.rowIdx, colIdx: Infinity }
    });
  }

  private addCellRangesForRemovedColumn(cell: DataGrid.Cell): void {
    if (cell.rowIdx > 0) {
      this.selection.cellRanges.push({
        begin: { rowIdx: 0, colIdx: cell.colIdx },
        end: { rowIdx: cell.rowIdx - 1, colIdx: cell.colIdx }
      });
    }

    this.selection.cellRanges.push({
      begin: { rowIdx: cell.rowIdx + 1, colIdx: cell.colIdx },
      end: { rowIdx: Infinity, colIdx: cell.colIdx }
    });
  }

  // Configuration options
  public selectionBehavior: SelectionBehavior = SelectionBehavior.SelectCell;
  public selectColumnOnHeaderClick: boolean = true;

  public handleSelectionEvent = (e: DataGrid.CellSelectionEvent): boolean => {
    const cell = e.cell;
    const { colIdx, rowIdx } = cell;

    if (this.selectionBehavior === SelectionBehavior.None || (colIdx >= 0 && rowIdx === -1 && !this.selectColumnOnHeaderClick)) { return false; }
    const rowSelection = colIdx === -1 || (colIdx >= 0 && rowIdx >= 0 && this.selectionBehavior === SelectionBehavior.SelectRow);
    const colSelection = rowIdx === -1 || (colIdx >= 0 && rowIdx >= 0 && this.selectionBehavior === SelectionBehavior.SelectColumn);

    const mouseButtons = e.buttons;
    const dragSelect = mouseButtons === 1 && e.type === 'mouseenter';

    const key = e.key;
    const arrow: boolean = !!key && key.indexOf('Arrow') === 0;
    const space: boolean = !!key && key === ' ';
    const esc: boolean = !!key && key === 'Escape';
    const ctrl: boolean = e.ctrlKey;
    const shift: boolean = e.shiftKey || dragSelect;
    const ctrlA: boolean = (ctrl && !!key && (key === 'a' || key === 'A'));

    if (arrow && ctrl && !shift) {
      return false;
    } else if (!!mouseButtons && mouseButtons !== 1) {
      return false;
    } else if (esc) {
      this.clearSelection();
    } else if (ctrlA) {
      this.selectAll();
    } else if (rowSelection && colSelection) {
      if (ctrl && !shift && IndexBasedGridSelection.areAllCellsInGridSelected(this.selection)) {
        this.clearSelection();
      } else {
        this.selectAll();
      }
    } else if (shift) {
      if (!ctrl) {
        this.clearSelection();
      }
      rowSelection ? this.rangeRowSelectFromAnchor(rowIdx) : colSelection ? this.rangeColSelectFromAnchor(colIdx) : this.rangeCellSelectFromAnchor(cell);
    } else {
      this.setAnchorPoint(cell);
      if (ctrl) {
        rowSelection ? this.toggleRowSelect(rowIdx) : colSelection ? this.toggleColumnSelect(colIdx) : this.toggleCellSelect(cell);
      } else {
        if (space) {
          rowSelection ? this.inclusiveRowSelect(rowIdx) : colSelection ? this.inclusiveColSelect(colIdx) : this.inclusiveCellSelect(cell);
        } else {
          rowSelection ? this.exclusiveRowSelect(rowIdx) : colSelection ? this.exclusiveColSelect(colIdx) : this.exclusiveCellSelect(cell);
        }
      }
    }
    return true;
  }
}
